import { useEffect, useId, useMemo, useRef, useState } from "react";
import { type Novel, systemOwnerId, translationLanguages } from "@lorevok/domain";
import {
  EMPTY_STATS,
  likeNovel,
  loadAuthorProfile,
  loadNovelForReading,
  publishNovel,
  unlikeNovel,
  type NovelStats,
  type NovelSummary
} from "../apiClient.js";
import { assetUrl, fallbackCoverUrl } from "../lib/novel.js";
import { AsyncButton } from "../components/AsyncButton.js";
import { AuthorByline } from "../components/AuthorByline.js";
import { CineBackdrop } from "../components/CineBackdrop.js";
import { ReelChipSkeleton } from "../components/ReelChipSkeleton.js";
import { CommentsSection } from "../components/CommentsSection.js";
import { ReportDialog } from "../components/ReportDialog.js";
import { FilmCard } from "../components/FilmCard.js";
import { FlagIcon } from "../components/FlagIcon.js";
import { localizeGenre } from "../lib/storyOptions.js";
import { track } from "../lib/analytics.js";
import { useNovelShare } from "../lib/shareNovel.js";
import { HeartIcon, ShareIcon } from "../components/EngagementIcons.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { POSTER_ASPECT, POSTER_CSS_WIDTH, fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";
import { NOVEL_TRANSLATION_ENABLED } from "../lib/features.js";
import { apiErrorMessage } from "../lib/apiErrorMessage.js";
import { missingPageMeta, novelPageMeta, usePageMeta } from "../lib/pageMeta.js";
import { languageName } from "../lib/languageNames.js";

/**
 * Estimated reading time in whole minutes across every scene's prose. The rate
 * is per-script — CJK text packs far more meaning per character — so a Japanese
 * and an English novel of similar length read as similar times. Returns 0 when
 * there is no prose (a trimmed payload), so the caller omits the chip.
 */
function estimateReadingMinutes(novel: Novel): number {
  let total = 0;
  let cjk = 0;
  for (const scene of novel.scenes) {
    const text = scene.text ?? "";
    total += text.length;
    for (const ch of text) {
      const cp = ch.codePointAt(0) ?? 0;
      // CJK punctuation + kana, CJK ideographs, Hangul syllables, fullwidth forms.
      if (
        (cp >= 0x3000 && cp <= 0x30ff) ||
        (cp >= 0x3400 && cp <= 0x9fff) ||
        (cp >= 0xac00 && cp <= 0xd7af) ||
        (cp >= 0xff00 && cp <= 0xffef)
      ) {
        cjk += 1;
      }
    }
  }
  if (total === 0) return 0;
  const rate = cjk / total > 0.3 ? 450 : 900;
  return Math.max(1, Math.ceil(total / rate));
}

/** Total number of choices across every scene — the story's branchiness. */
function countBranches(novel: Novel): number {
  return novel.scenes.reduce((sum, scene) => sum + (scene.choices?.length ?? 0), 0);
}

/**
 * A short, spoiler-light teaser taken from the very opening of the story (the
 * start scene's first sentence or two, capped so it reads as a tagline). Null
 * when there is no prose to quote.
 */
function openingTeaser(novel: Novel): string | null {
  const scene =
    novel.scenes.find((candidate) => candidate.id === novel.start_scene_id) ?? novel.scenes[0];
  const text = (scene?.text ?? "").replace(/\s+/gu, " ").trim();
  if (!text) return null;
  const max = 220;
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastStop = Math.max(
    slice.lastIndexOf(". "),
    slice.lastIndexOf("! "),
    slice.lastIndexOf("? "),
    slice.lastIndexOf("… ")
  );
  if (lastStop > 90) return slice.slice(0, lastStop + 1);
  const lastSpace = slice.lastIndexOf(" ");
  return `${(lastSpace > 90 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
}

export function NovelDetailPage({
  novelId,
  currentOwnerId,
  onPlay,
  onEdit,
  onOpenAuthor,
  onOpenDetail,
  onPublished,
  onBack,
  onRequireAccount
}: {
  novelId: string;
  /** The signed-in user's id, or null when they can't comment (must sign in). */
  currentOwnerId: string | null;
  onPlay: (novelId: string) => void;
  /** Opens the editor for this novel (owner-only control). */
  onEdit: (novelId: string) => void;
  onOpenAuthor: (ownerId: string, username?: string) => void;
  onOpenDetail: (novelId: string) => void;
  /** Notifies the App a draft was published here so it can reconcile the lists. */
  onPublished?: (published: Novel) => void;
  onBack: () => void;
  /**
   * A signed-out visitor tried an account-only action (like, comment): take
   * them to sign up, and back to this novel afterwards.
   */
  onRequireAccount?: (reason: "like" | "comment") => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // The API gave no answer (offline, a 5xx): the page shows "not loaded", but
  // that is a blip, not a verdict search engines should hear.
  const [unavailable, setUnavailable] = useState(false);
  const [stats, setStats] = useState<NovelStats>(EMPTY_STATS);
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | undefined>(undefined);
  const [ownerUsername, setOwnerUsername] = useState<string | undefined>(undefined);
  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);
  const [likePending, setLikePending] = useState(false);
  const { share: shareNovel, copied: shareCopied } = useNovelShare("detail");
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // The novel on screen, so a re-read after the account changes (sign-out, or a
  // sign-in in another tab) refreshes the heart and counters in place instead
  // of flashing the loading skeleton.
  const shownNovelId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (shownNovelId.current !== novelId) {
      setLoading(true);
      setNotFound(false);
      setUnavailable(false);
      setLiked(false);
    }

    void loadNovelForReading(novelId).then((result) => {
      if (cancelled) {
        return;
      }
      // loadNovel falls back to the demo novel on any failure. If we asked for a
      // different id and got the demo back, the real novel could not be fetched.
      if (result.source === "fallback" && result.novel.id !== novelId) {
        shownNovelId.current = null;
        setNotFound(true);
        setUnavailable(result.unavailable === true);
        setNovel(null);
      } else {
        shownNovelId.current = novelId;
        setNovel(result.novel);
        setStats(result.stats);
        setOwnerDisplayName(result.ownerDisplayName);
        setOwnerUsername(result.ownerUsername);
        setOwnerAvatarUrl(result.ownerAvatarUrl ?? null);
        // One like per account: the heart is the server's answer for the
        // account that asked, so it is the same on every browser and device.
        setLiked(result.likedByMe === true);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // The like state belongs to the account, so re-read when it changes.
  }, [novelId, currentOwnerId]);

  // The head: the novel's title, description, canonical and language once it
  // is here; a novel the API said is missing stays out of the index. When the
  // API merely could not answer, the route layer's head stays (canonical, no
  // noindex): a blip must not drop a healthy page from search results.
  usePageMeta(
    loading
      ? null
      : notFound || !novel
        ? unavailable
          ? null
          : missingPageMeta(t("detail.notLoadedTitle"), t)
        : novelPageMeta(novel, t)
  );

  // Share the novel's short link: the native share sheet when the browser has
  // one (mobile / PWA), otherwise copy it and confirm with a short-lived toast.
  function share() {
    if (novel) void shareNovel(novel);
  }

  async function toggleLike() {
    if (likePending) return;
    // Liking needs an account; a signed-out visitor is sent to sign up instead
    // of seeing the heart flip on and silently roll back.
    if (currentOwnerId === null && onRequireAccount) {
      onRequireAccount("like");
      return;
    }
    const next = !liked;
    if (next) track("novel_liked", { novel_id: novelId, surface: "detail" });
    setLikePending(true);
    // Optimistic: flip the heart and count immediately, reconcile with the server.
    setLiked(next);
    setStats((current) => ({ ...current, likes: Math.max(0, current.likes + (next ? 1 : -1)) }));
    try {
      const updated = next ? await likeNovel(novelId) : await unlikeNovel(novelId);
      setStats(updated.stats);
      setLiked(updated.liked);
    } catch {
      // Roll back on failure.
      setLiked(!next);
      setStats((current) => ({ ...current, likes: Math.max(0, current.likes + (next ? -1 : 1)) }));
    } finally {
      setLikePending(false);
    }
  }

  // Publish this draft (owner-only). The detail page is where a novel's owner
  // manages it now, so publishing happens here rather than on the list page.
  async function publish() {
    if (!novel) return;
    setPublishError(null);
    setPublishing(true);
    try {
      const published = await publishNovel(novel.id);
      setNovel(published);
      onPublished?.(published);
    } catch (error) {
      setPublishError(apiErrorMessage(error, t, t("profile.publishFailed")));
    } finally {
      setPublishing(false);
    }
  }

  if (loading) {
    // Skeleton that mirrors the loaded title-hero so content doesn't jump in. The
    // shimmer is aria-hidden; the role="status" line is the accessible signal.
    // The back placeholder carries .reel-back so the page's top-padding trim
    // (.page-section:has(> .reel-back)) applies here too — otherwise the whole
    // hero drops ~35px lower than the loaded one and jumps up on load.
    return (
      <section className="page-section novel-detail" aria-busy="true">
        <p className="sr-only" role="status">{t("detail.loading")}</p>
        <div className="skeleton detail-back-skeleton reel-back" aria-hidden="true" />
        <div className="title-hero" aria-hidden="true">
          <div className="title-hero-inner">
            <div className="title-poster skeleton" />
            <div className="title-credits">
              <div className="title-headline">
                <div className="skeleton detail-eyebrow-skeleton" />
                <div className="skeleton detail-title-skeleton" />
                <div className="skeleton skeleton-line skeleton-line--short" />
              </div>
              <div className="title-synopsis">
                <div className="skeleton skeleton-line" />
                <div className="skeleton skeleton-line" />
                <div className="skeleton skeleton-line" />
                <div className="skeleton skeleton-line skeleton-line--short" />
              </div>
              <div className="detail-cta">
                {/* Watch, then like · share · report. */}
                <div className="button-row">
                  <div className="skeleton detail-buttons-skeleton" />
                  <div className="skeleton detail-icon-skeleton" />
                  <div className="skeleton detail-icon-skeleton" />
                  <div className="skeleton detail-icon-skeleton" />
                </div>
                <div className="skeleton detail-views-skeleton" />
              </div>
            </div>
          </div>
        </div>
        <div className="title-meta reel-chip-row" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, index) => (
            <ReelChipSkeleton key={index} />
          ))}
        </div>
      </section>
    );
  }

  if (notFound || !novel) {
    return (
      <section className="page-section">
        <div className="section-heading">
          <p className="eyebrow">{t("detail.eyebrow")}</p>
          <h1>{t("detail.notLoadedTitle")}</h1>
          <p className="metrics">{t("detail.notLoadedBody")}</p>
        </div>
        <button type="button" className="detail-back-btn reel-back" onClick={onBack}>
          {t("detail.back")}
        </button>
      </section>
    );
  }

  return (
    <NovelDetailView
      novel={novel}
      stats={stats}
      ownerDisplayName={ownerDisplayName}
      ownerUsername={ownerUsername}
      ownerAvatarUrl={ownerAvatarUrl}
      liked={liked}
      likePending={likePending}
      shareCopied={shareCopied}
      lang={lang}
      currentOwnerId={currentOwnerId}
      publishing={publishing}
      publishError={publishError}
      onPlay={onPlay}
      onEdit={onEdit}
      onPublish={publish}
      onOpenAuthor={onOpenAuthor}
      onOpenDetail={onOpenDetail}
      onBack={onBack}
      onToggleLike={toggleLike}
      onShare={share}
      onRequireCommentAccount={onRequireAccount ? () => onRequireAccount("comment") : undefined}
    />
  );
}

/** The loaded detail layout — split out so the derived film metadata memoizes
 *  against a guaranteed-present novel. */
function NovelDetailView({
  novel,
  stats,
  ownerDisplayName,
  ownerUsername,
  ownerAvatarUrl,
  liked,
  likePending,
  shareCopied,
  lang,
  currentOwnerId,
  publishing,
  publishError,
  onPlay,
  onEdit,
  onPublish,
  onOpenAuthor,
  onOpenDetail,
  onBack,
  onToggleLike,
  onShare,
  onRequireCommentAccount
}: {
  novel: Novel;
  stats: NovelStats;
  ownerDisplayName: string | undefined;
  ownerUsername: string | undefined;
  ownerAvatarUrl: string | null;
  liked: boolean;
  likePending: boolean;
  shareCopied: boolean;
  lang: string;
  currentOwnerId: string | null;
  publishing: boolean;
  publishError: string | null;
  onPlay: (id: string) => void;
  onEdit: (id: string) => void;
  onPublish: () => void;
  onOpenAuthor: (id: string, username?: string) => void;
  onOpenDetail: (id: string) => void;
  onBack: () => void;
  onToggleLike: () => void;
  onShare: () => void;
  onRequireCommentAccount?: () => void;
}) {
  const isOwner = currentOwnerId !== null && novel.owner_id === currentOwnerId;
  const t = useT();
  const ownerLabelId = useId();
  const [reporting, setReporting] = useState(false);
  const cover = assetUrl(novel, novel.cover_asset_id) || fallbackCoverUrl;
  const translated = NOVEL_TRANSLATION_ENABLED ? translationLanguages(novel) : [];
  const endingCount = novel.endings.length;
  const teaser = openingTeaser(novel);

  const meta = useMemo(() => {
    const runtime = estimateReadingMinutes(novel);
    const chips: Array<{ figure: string; label: string }> = [];
    if (runtime > 0)
      chips.push({ figure: t("detail.runtimeValue", { count: runtime }), label: t("detail.runtime") });
    if (novel.scenes.length > 0)
      chips.push({ figure: novel.scenes.length.toLocaleString(lang), label: t("detail.scenes") });
    if (endingCount > 0)
      chips.push({ figure: endingCount.toLocaleString(lang), label: t("detail.endings") });
    const branches = countBranches(novel);
    if (branches > 0)
      chips.push({ figure: branches.toLocaleString(lang), label: t("detail.branches") });
    // The novel's language tag; with translation on, how many it reads in.
    chips.push(
      NOVEL_TRANSLATION_ENABLED
        ? { figure: (translated.length + 1).toLocaleString(lang), label: t("detail.languages") }
        : { figure: languageName(novel.language, lang), label: t("detail.language") }
    );
    return chips;
  }, [novel, lang, t, endingCount, translated.length]);

  // "More from this author" — the author's other published novels. Skipped for
  // the built-in system/demo owner (no real author page behind it).
  const [otherNovels, setOtherNovels] = useState<NovelSummary[]>([]);
  useEffect(() => {
    if (!novel.owner_id || novel.owner_id === systemOwnerId) {
      setOtherNovels([]);
      return;
    }
    let cancelled = false;
    void loadAuthorProfile(novel.owner_id).then((profile) => {
      if (cancelled) return;
      setOtherNovels(profile.novels.filter((candidate) => candidate.id !== novel.id).slice(0, 8));
    });
    return () => {
      cancelled = true;
    };
  }, [novel.id, novel.owner_id]);

  return (
    <section className="page-section novel-detail">
      <button type="button" className="detail-back-btn reel-back" onClick={onBack}>
        {t("detail.back")}
      </button>

      <div className="title-hero">
        <CineBackdrop cover={cover} />
        {/* Author-only management strip across the top of the hero: status on
            the left, Edit / Publish on the right — kept apart from the reader's
            Play / like / share row, and in flow so it never overlaps the poster. */}
        {isOwner && (
          <div className="detail-owner-bar" role="group" aria-labelledby={ownerLabelId}>
            <div className="detail-owner-status">
              <span id={ownerLabelId} className="detail-owner-label">
                {t("detail.ownerLabel")}
              </span>
              {novel.visibility === "public" && (
                <span className="badge badge--public">{t("editor.publishedBadge")}</span>
              )}
              {novel.visibility === "draft" && (
                <span className="badge badge--draft">{t("library.draftBadge")}</span>
              )}
            </div>
            <div className="detail-owner-buttons">
              <button
                type="button"
                className="detail-owner-btn detail-edit"
                onClick={() => onEdit(novel.id)}
              >
                <PencilIcon />
                {t("profile.edit")}
              </button>
              {novel.visibility === "draft" && (
                <AsyncButton
                  className="detail-owner-btn detail-publish"
                  busy={publishing}
                  loadingLabel={t("profile.publishing")}
                  onClick={() => onPublish()}
                >
                  <PublishIcon />
                  {t("profile.publish")}
                </AsyncButton>
              )}
            </div>
            {publishError && (
              <p className="invalid detail-publish-error" role="alert">
                {publishError}
              </p>
            )}
          </div>
        )}
        <div className="title-hero-inner">
          <div className="title-poster poster-frame">
            <img src={sizedImageUrl(cover, POSTER_CSS_WIDTH, { aspect: POSTER_ASPECT })} onError={fallBackToSource(cover)} alt={novel.title} fetchPriority="high" />
          </div>

          <div className="title-credits">
            <div className="title-headline">
              <p className="marquee-eyebrow">
                <span className="reel-dot" aria-hidden="true" />
                {t("detail.eyebrow")}
              </p>
              <div className="title-badges">
                <h1>{novel.title}</h1>
                <span className="badge badge--ai" title={t("ai.badgeTitle")}>
                  {t("ai.badge")}
                </span>
                {translated.length > 0 && (
                  <span
                    className="badge badge--localized"
                    title={t("common.localizedIn", { languages: translated.join(", ") })}
                  >
                    {t("common.localized")}
                  </span>
                )}
              </div>

              {ownerDisplayName && (
                <p className="detail-directed">
                  <span className="detail-directed-label">{t("detail.writtenBy")}</span>
                  <AuthorByline
                    ownerId={novel.owner_id}
                    username={ownerUsername}
                    displayName={ownerDisplayName}
                    avatarUrl={ownerAvatarUrl}
                    onOpenAuthor={onOpenAuthor}
                    className="detail-byline"
                  />
                </p>
              )}

              {novel.genre.length > 0 && (
                <div className="tag-row">
                  {novel.genre.map((tag) => (
                    <span key={tag}>{localizeGenre(tag, t)}</span>
                  ))}
                </div>
              )}
            </div>

            {novel.description && (
              <div className="title-synopsis">
                <span className="title-synopsis-label">{t("detail.synopsis")}</span>
                <p className="detail-description">{novel.description}</p>
              </div>
            )}

            <div className="detail-cta">
              <div className="button-row detail-buttons">
                <button
                  type="button"
                  className="detail-play-btn detail-watch"
                  onClick={() => onPlay(novel.id)}
                >
                  <PlayIcon />
                  {t("detail.play")}
                </button>
                <button
                  type="button"
                  className={
                    liked
                      ? "detail-icon-btn detail-like detail-like--active"
                      : "detail-icon-btn detail-like"
                  }
                  aria-pressed={liked}
                  aria-label={liked ? t("detail.liked") : t("detail.like")}
                  title={liked ? t("detail.liked") : t("detail.like")}
                  disabled={likePending}
                  onClick={() => onToggleLike()}
                >
                  <HeartIcon filled={liked} />
                  <span className="detail-like-count">{stats.likes}</span>
                </button>
                <button
                  type="button"
                  className="detail-icon-btn detail-share"
                  aria-label={t("detail.share")}
                  title={t("detail.share")}
                  onClick={() => onShare()}
                >
                  <ShareIcon />
                </button>
                {/* Anyone can report (EU DSA art. 16) — just not their own novel. */}
                {!isOwner && (
                  <button
                    type="button"
                    className="detail-icon-btn detail-report"
                    aria-label={t("report.novelAction")}
                    title={t("report.novelAction")}
                    onClick={() => setReporting(true)}
                  >
                    <FlagIcon />
                  </button>
                )}
              </div>
              {reporting && (
                <ReportDialog
                  targetType="novel"
                  targetId={novel.id}
                  novelId={novel.id}
                  onClose={() => setReporting(false)}
                />
              )}
              <p className="detail-share-toast" role="status" aria-live="polite">
                {shareCopied ? t("detail.shareCopied") : ""}
              </p>
              <div className="detail-engagement">
                <span title={t("detail.viewsTitle", { count: stats.views })}>
                  ◉ {t("detail.viewsLabel", { count: stats.views })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {meta.length > 0 && (
        <div className="title-meta reel-chip-row">
          {meta.map((chip) => (
            <div className="reel-chip" key={chip.label}>
              <span className="reel-chip-figure">{chip.figure}</span>
              <span className="reel-chip-label">{chip.label}</span>
            </div>
          ))}
        </div>
      )}

      {teaser && (
        <section className="detail-section teaser-section">
          <div className="detail-section-label">
            <span className="detail-section-title">{t("detail.prologue")}</span>
          </div>
          <blockquote className="teaser-quote">{teaser}</blockquote>
        </section>
      )}

      {otherNovels.length > 0 && (
        <section className="detail-section">
          <div className="detail-section-label">
            <span className="detail-section-title">{t("detail.moreFromAuthor")}</span>
          </div>
          <div className="more-rail">
            {otherNovels.map((other) => (
              <div className="more-rail-item" key={other.id}>
                <FilmCard novel={other} lang={lang} t={t} onPlay={onPlay} onOpenDetail={onOpenDetail} />
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="reviews-header">
        <CommentsSection
          novelId={novel.id}
          novelOwnerId={novel.owner_id}
          currentUserId={currentOwnerId}
          onOpenAuthor={onOpenAuthor}
          onRequireAccount={onRequireCommentAccount}
        />
      </div>
    </section>
  );
}

/** Play triangle for the primary "Watch" call to action. */
function PlayIcon() {
  return (
    <svg
      className="detail-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

/** Pencil glyph for the owner's Edit button. */
function PencilIcon() {
  return (
    <svg
      className="detail-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
      <path d="m13.5 6.5 4 4" />
    </svg>
  );
}

/** Upload-arrow glyph for the owner's Publish button (drafts only). */
function PublishIcon() {
  return (
    <svg
      className="detail-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 15V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}
